import {
  BreadcrumbComponent,
  Button,
  CIActionGuard,
  CIAppliedFilter,
  CIAppliedListFilter,
  CIPageHeader,
  CITableGrid,
  getLocaleDateTimeFormat,
  Loader,
  NoDataAvailable,
  SearchInput,
  toLocalDate,
  toUtcDate,
  UnableToFetch,
  useUserPermission,
  usePaginationPreference
} from 'ccs-ui-components';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { requiredAuthPermission } from '../../../../../common/constants/UserPermissionConstants';
import { IExceptionSearchModel } from '../../../../models/index';
import { fetchAllExceptionListRequest } from '../../../../store/actions/sagaActions/exceptions/ExceptionListSagaAction';
import { AppDispatch, useSelector } from '../../../../store/AppStore';

import { URL_PATH, UTCFILTER, SORT_TYPE } from '../../../../constants';
import { ApiModifySort, ExceptionListFilterData, viewHelperText } from '../../TypeDefs';
import ExceptionListFilter from '../ExceptionListFilter';
import classes from './ExceptionList.module.scss';
import { addFilterExceptionData, addSortExceptionData } from '../../../../store/reducer/exceptions/ExceptionFilterReducer';
import { getFilterParams, renderExceptionIconButtons } from '../../../../utilities/exceptions/utility';

const ExceptionTable = () => {
  // hook variables
  const route = useLocation();
  const { permissionObject } = useUserPermission();
  const { t: translate } = useTranslation();
  const history = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { getPreferredPageSize, persistPageSize } = usePaginationPreference();

  // local variables
  const lastModified = SORT_TYPE.UPDATED_TIMESTAMP;
  const updatedBy = 'updatedBy';
  const debounceDelay = 300;
  const minItemSize = 10;

  const { searchText: reduxSearch, status: reduxStatus, fromDate: reduxFrom, toDate: reduxTo, sort: reduxSort } = useSelector((state) => state.filterExceptionReducer);

  const params = useMemo(
    () => getFilterParams(route.search, { searchText: reduxSearch, status: reduxStatus, fromDate: reduxFrom, toDate: reduxTo, sort: reduxSort }),
    [route.search, reduxSearch, reduxStatus, reduxFrom, reduxTo, reduxSort]
  );

  // state variables
  const [queryParams, setQueryParams] = useState<{ [key: string]: string }>(params);
  const [page, setPage] = useState(queryParams.page ? parseInt(queryParams.page, minItemSize) : 0);
  const [item, setItem] = useState(queryParams.item ? Number(queryParams.item) : getPreferredPageSize());
  const [searchText, setSearchText] = useState(queryParams.exceptionNames ?? '');
  const [validFromDate, setValidFromDate] = useState<string>('');
  const [validToDate, setValidToDate] = useState<string>('');
  const [validType, setValidType] = useState<string[]>(queryParams.exceptionTypes?.split(',') ?? []);
  const [sortedName, setSortedName] = useState(lastModified);
  const [sortedOrder, setSortedOrder] = useState(ApiModifySort.DESC);
  const [updatedBySort, setUpdatedBySort] = useState<string>();
  const [sortParameter, setSortParameter] = useState<string>(`${lastModified},${ApiModifySort.DESC}`);
  const [lastModifiedSort, setLastModifiedSort] = useState<string>(
    ApiModifySort.DESC
  );

  /**
 * @description object defined for different status roles.
 */
  const statusTypes = [
    { value: translate('list_exception.types_business'), label: translate('list_exception.types_business') },
    { value: translate('list_exception.types_system'), label: translate('list_exception.types_system') },
    { value: translate('list_exception.type_data_quality'), label: translate('list_exception.type_data_quality') },
  ];
  const allStatusMap: { [key: string]: string } = {};
  statusTypes.forEach((s) => { allStatusMap[s.value] = s.label; });

  const [filterData, setFilterData] = useState<ExceptionListFilterData>();
  const appliedFilterData: CIAppliedFilter[] = useMemo(() => {
    const data: CIAppliedFilter[] = [];
    if (!filterData) {
      return [];
    }
    if (filterData.status.length > 0 && filterData.status.length < statusTypes.length) {
      filterData.status.forEach((f) => data.push({ text: allStatusMap[f], type: 'status', value: f }));
    }
    if (filterData.startDate && filterData.endDate) {
      data.push({ value: filterData.endDate, type: 'date', text: `${translate('list_exception.txt_last_mod')}: ${toLocalDate({ date: filterData.startDate, dateTimeFormat: { date: true } })} - ${toLocalDate({ date: filterData.endDate, dateTimeFormat: { date: true } })}` });
    } else if (filterData.startDate) {
      data.push({ value: filterData.startDate, type: 'start_date', text: `${translate('list_exception.txt_last_modified_from')}: ${toLocalDate({ date: filterData.startDate, dateTimeFormat: { date: true } })}` });
    } else if (filterData.endDate) {
      data.push({ value: filterData.endDate, type: 'end_date', text: `${translate('list_exception.txt_last_modified_to')}: ${toLocalDate({ date: filterData.endDate, dateTimeFormat: { date: true } })}` });
    }
    return data;
  }, [filterData]);

  const isShowingDefaultFilter = useMemo(
    () => {
      const filterStatusList: string[] = filterData?.status ?? [];
      return filterStatusList.length === 0 && !filterData?.startDate && !filterData?.endDate;
    },
    [filterData]
  );

  const { exception, isLoading, eventErrorMessage } = useSelector((state) => state.fetchAllExceptionList);

  const queryParam = {
    exceptionNames: [searchText],
    exceptionTypes: validType?.length > 0 ? validType : [],
    fromDate: validFromDate,
    toDate: validToDate,
    status: ['active']
  };

  /**
   * @method lastModifiedSortToggleHandler
   * @description sorts the list by last modified (Ascending or Descending)
   */
  const lastModifiedSortToggleHandler = () => {
    setSortedName(lastModified);
    if (lastModifiedSort === ApiModifySort.ASC) {
      setLastModifiedSort(ApiModifySort.DESC);
      setSortedOrder(ApiModifySort.DESC);
      setSortParameter(`${lastModified},${ApiModifySort.DESC}`);
      setQueryParams((pre) => ({
        ...pre,
        sort: lastModified,
        type: ApiModifySort.DESC
      }));
    } else {
      setLastModifiedSort(ApiModifySort.ASC);
      setSortedOrder(ApiModifySort.ASC);
      setSortParameter(`${lastModified},${ApiModifySort.ASC}`);
      setQueryParams((pre) => ({
        ...pre,
        sort: lastModified,
        type: ApiModifySort.ASC
      }));
    }
  };

  /**
   * @method updatedBySortToggleHandler
   * @description sorts the list by updated by (Ascending or Descending)
   */
  const updatedBySortToggleHandler = () => {
    setSortedName(updatedBy);
    if (updatedBySort === ApiModifySort.ASC) {
      setUpdatedBySort(ApiModifySort.DESC);
      setSortedOrder(ApiModifySort.DESC);
      setSortParameter(`${updatedBy},${ApiModifySort.DESC}`);
      setQueryParams((pre) => ({
        ...pre,
        sort: updatedBy,
        type: ApiModifySort.DESC
      }));
    } else {
      setUpdatedBySort(ApiModifySort.ASC);
      setSortedOrder(ApiModifySort.ASC);
      setSortParameter(`${updatedBy},${ApiModifySort.ASC}`);
      setQueryParams((pre) => ({
        ...pre,
        sort: updatedBy,
        type: ApiModifySort.ASC
      }));
    }
  };

  /**
   * @method onGetPageNumber
   * @description handles the pages of the list (previous page or next page)
   * @param page
   */
  const onGetPageNumber = (page: number) => {
    setPage(page);
    setQueryParams((pre) => ({
      ...pre,
      page: String(page)
    }));
  };

  /**
   * @method onGetItemsPerPage
   * @description handles the item to show in the page
   * @param item
   */
  const onGetItemsPerPage = (item: number) => {
    setItem(item);
    setQueryParams((pre) => ({
      ...pre,
      item: String(item)
    }));
  };

  /**
   * @method handleNewException
   * @description button function that takes you to create-exception page
   */
  const handleNewException = () => {
    history(`${URL_PATH.CONTROL_PLANE}${URL_PATH.EXCEPTION_CREATE}`, { state: { index: 0 } });
  };

  /**
  * View button click handler
  */
  const viewBtnClickHandler = (data: IExceptionSearchModel) => {
    history(`${URL_PATH.CONTROL_PLANE}${URL_PATH.EXCEPTION_VIEW}/${data.ersExceptionName}/${data.version}`, { state: { search: route.search } });
  };

  /**
   * Edit button click handler
   */
  const editBtnClickHandler = (data: IExceptionSearchModel) => {
    history(`${URL_PATH.CONTROL_PLANE}${URL_PATH.EXCEPTION_EDIT}/${data.ersExceptionName}/${data.version}`, { state: { search: route.search } });
  };

  /**
   * @method debounceHandler
   * @description delay after the filtered exception names
   */
  const debounceHandler = useCallback(_.debounce((value: string) => {
    if (value.length > 0) {
      const payload = {
        exceptionNames: [value],
        exceptionTypes: validType?.length > 0 ? validType : [],
        fromDate: validFromDate,
        toDate: validToDate,
        status: queryParam.status
      };

      dispatch(fetchAllExceptionListRequest(page, item, payload, sortParameter));
    }
  }, debounceDelay), [searchText]);

  /**
   * @method handleSearchText
   * @description handles the search text
   * @param event
   */
  const handleSearchText = (event: React.ChangeEvent<HTMLInputElement>) => {
    const data = {
      exceptionNames: (event.target.value).trim(),
      exceptionTypes: queryParam.exceptionTypes.join(','),
      page: String(0),
      fromDate: queryParam.fromDate,
      toDate: queryParam.toDate
    };
    setQueryParams(data);
    setPage(0);
    setSearchText((event.target.value).trim());
    debounceHandler((event.target.value).trim());
  };

  /**
   * @method setFilterDate
   * @description Sets the filter date based on the query parameters.
   */
  const setFilterDate = () => {
    const formateFromDate = queryParams.fromDate === undefined || queryParams.fromDate === '' ? '' : `${toUtcDate({ date: queryParams.fromDate }).replace(' ', 'T')}${UTCFILTER.UTC_FROM_TIMESTAMP_EXTENSION_STR}`;
    const formateToDate = queryParams.toDate === undefined || queryParams.toDate === '' ? '' : `${toUtcDate({ date: queryParams.toDate }).replace(' ', 'T')}${UTCFILTER.UTC_TO_TIMESTAMP_EXTENSION_STR}`;
    setValidFromDate(formateFromDate);
    setValidToDate(formateToDate);
  };

  /**
   * @method setFilterStates
   * @description sets the filtered state values
   */
  const setFilterStates = () => {
    const setLastModifiedSortCondition = !queryParams.sort ? ApiModifySort.DESC : '';
    setSearchText(queryParam?.exceptionNames && queryParam?.exceptionNames.length > 0 ? queryParam.exceptionNames[0] : '');
    setPage(queryParams.page === undefined ? Number('') : Number(queryParams.page));
    setLastModifiedSort(
      queryParams.sort === lastModified
        ? queryParams.type
        : setLastModifiedSortCondition
    );
    setSortParameter(
      !queryParams.sort
        ? `${lastModified},${ApiModifySort.DESC}`
        : `${queryParams.sort},${queryParams.type}`
    );
    setFilterDate();
    const filter: ExceptionListFilterData = { status: [] };

    setValidType(queryParams.exceptionTypes ? queryParams.exceptionTypes.split(',') : []);

    if (queryParams.exceptionTypes && queryParams.exceptionTypes.length > 0) {
      filter.status = queryParams.exceptionTypes?.split(',');
    } else {
      filter.status = [];
    }
    filter.startDate = queryParams.fromDate;
    filter.endDate = queryParams.toDate;

    setFilterData(filter);

    // sync Redux filter and sort for consistency with services/events
    const sortStr = !queryParams.sort ? `${lastModified},${ApiModifySort.DESC}` : `${queryParams.sort},${queryParams.type}`;
    const typesCsv = queryParams.exceptionTypes ?? '';
    const fromDate = queryParams.fromDate ?? '';
    const toDate = queryParams.toDate ?? '';
    const searchVal = queryParams.exceptionNames ?? '';
    dispatch(addSortExceptionData(sortStr));
    dispatch(addFilterExceptionData({ searchText: searchVal, status: typesCsv, fromDate, toDate }));
  };

  /**
   * @method filterHandler
   * @description handles the filter for the Exception list page
   */
  const filterHandler = (data: ExceptionListFilterData) => {
    setPage(0);
    setQueryParams((pre) => ({
      ...pre,
      exceptionNames: searchText,
      exceptionTypes: data.status.join(','),
      page: String(0),
      fromDate: data.startDate ?? '',
      toDate: data.endDate ?? '',
    }));

    setFilterDate();
    setValidType(data.status);
  };
  /**
   * @method appliedFilterChangeHandler
   * @description Event handler for applied filter change.
   * @param {CIAppliedFilter[]} filters - The applied filters.
   */
  const appliedFilterChangeHandler = (filters: CIAppliedFilter[]) => {
    const statusList: string[] = [];
    let hasStartDate = false;
    let hasEndDate = false;
    filters.forEach((f) => {
      if (f.type === viewHelperText.txt_status) {
        statusList.push(f.value);
      } else if (f.type === viewHelperText.txt_start_date) {
        hasStartDate = true;
      } else if (f.type === viewHelperText.txt_end_date) {
        hasEndDate = true;
      } else if (f.type === viewHelperText.txt_date) {
        hasStartDate = true;
        hasEndDate = true;
      }
    });
    const data: ExceptionListFilterData = {
      status: statusList,
      startDate: hasStartDate ? filterData?.startDate : undefined,
      endDate: hasEndDate ? filterData?.endDate : undefined
    };
    setPage(0);

    setQueryParams((pre) => ({
      ...pre,
      exceptionNames: searchText || '',
      exceptionTypes: data?.status.join(','),
      page: String(0),
      fromDate: data.startDate ?? '',
      toDate: data.endDate ?? '',
    }));
    setFilterDate();
    const payload = {
      exceptionNames: queryParam.exceptionNames ?? [],
      exceptionTypes: queryParam.exceptionTypes,
      fromDate: validFromDate,
      toDate: validToDate,
      status: queryParam.status
    };

    dispatch(fetchAllExceptionListRequest(page, item, payload, sortParameter));
  };

  const resetFiltersHandler = () => {
    setPage(0);
    setQueryParams((pre) => ({
      ...pre,
      exceptionTypes: '',
      fromDate: '',
      toDate: '',
      page: '0'
    }));
    setFilterDate();
  };

  const footerOptionsObj = {
    itemsPerPage: item,
    currentPageNumber: page,
    numberOfPages: exception.numberOfPages,
    onCurrentPageNumberChange: onGetPageNumber,
    onItemsPerPageChange: onGetItemsPerPage
  };

  const footerObj = { footerType: 'paging', footerOptions: footerOptionsObj };

  /**
   * @method renderExceptionName
   * @description Renders the exception name and description.
   * @param {IExceptionSearchModel} row - The exception search model.
   * @returns {React.ReactElement} The rendered exception name and description.
   */
  const renderExceptionName = (row: IExceptionSearchModel) => (
    <>
      <div className={classes['exc-name']} title={row.ersExceptionName} data-testid="exceptionName">{row.ersExceptionName}</div>
      <div className={classes['exc-desc']} title={row.ersExceptionDescription}>
        {row.ersExceptionDescription}
      </div>
    </>
  );

  /**
   * @method renderExceptionType
   * @description Renders the exception type.
   * @param {IExceptionSearchModel} row - The exception search model.
   * @returns {React.ReactElement} The rendered exception type.
   */
  const renderExceptionType = (row: IExceptionSearchModel) => (
    <div>{_.startCase(_.capitalize(row.ersExceptionType))}</div>
  );

  /**
   * @method renderExceptionDate
   * @description Renders the exception date.
   * @param {IExceptionSearchModel} row - The exception search model.
   * @returns {React.ReactElement} The rendered exception date.
   */
  const renderExceptionDate = (row: IExceptionSearchModel) => {
    const date = toLocalDate({ date: row.updatedTimestamp });
    return (
      <div className={classes['exc-date']} title={date}>{date}</div>
    );
  };

  /**
   * @method renderModifiedBy
   * @description Renders the modified by.
   * @param {IExceptionSearchModel} row - The exception search model.
   * @returns {React.ReactElement} The rendered modified by.
   */
  const renderModifiedBy = (row: IExceptionSearchModel) => (
    <div className={classes['exc-date']} title={row.updatedBy}>{row.updatedBy}</div>
  );

  useEffect(() => {
    if (Object.keys(queryParams)?.length !== 0) {
      const searchParams = new URLSearchParams();
      Object.entries(queryParams).forEach(([k, v]) => {
        if (v) searchParams.set(k, v);
      });

      setFilterStates();
      const queryString = searchParams.toString();
      history(queryString ? `?${queryString}` : route.search, { state: route.search });
    }
  }, [queryParams]);

  useEffect(() => {
    const itemSize = item <= minItemSize ? minItemSize : item;
    if (page >= 0 && sortParameter) {
      dispatch(fetchAllExceptionListRequest(page, itemSize, queryParam, sortParameter));
    }
  }, [item, page, validFromDate, validToDate, validType, sortParameter]);

  useEffect(() => {
    if (queryParams.item) {
      persistPageSize(Number(queryParams.item));
    }
  }, [queryParams.item]);

  return (
    <div className={classes['top-head']}>
      <BreadcrumbComponent />
      <div className={classes.head}>
        <CIPageHeader header={String(translate('list_exception.exception'))} />
      </div>
      <div className={classes.filters}>
        <div className={classes['search-filter-div']}>
          <ExceptionListFilter data={filterData} onApply={filterHandler} onResetFilters={resetFiltersHandler} allStatusList={statusTypes} />
          <div className={classes.search}>
            <SearchInput
              type="search"
              placeholder={String(translate('list_exception.txt_placeholder_search'))}
              value={searchText}
              onChange={handleSearchText}
              data-testid="searchException"
              label={undefined}
            />
          </div>

          <div className={classes.buttons}>
            <div>
              {CIActionGuard([requiredAuthPermission.CP.edit], permissionObject.permission) && (
                <Button
                  data-testid="createNewExceptionBtn"
                  variant="primary"
                  onClick={handleNewException}
                >{translate('list_exception.txt_create_exception')}
                </Button>)}
            </div>
          </div>
        </div>
        <CIAppliedListFilter
          className={classes['applied-filters']}
          totalRecords={exception.totalRecords}
          appliedFilters={appliedFilterData}
          onFilterChange={appliedFilterChangeHandler}
          onResetFilters={resetFiltersHandler}
          showResetFiltersButton={!isShowingDefaultFilter}
        />
      </div>
      {eventErrorMessage && (
        <div
          className={classes['error-container']}
          data-testid="ExceptionListError"
        >
          <UnableToFetch />
        </div>
      )}
      {exception.ersExceptions.length === 0 && (
        <div
          className={classes['error-container']}
          data-testid="ExceptionListError"
        >
          <NoDataAvailable />
        </div>
      )}
      <Loader show={isLoading} />
      {(!eventErrorMessage && exception.ersExceptions && exception.ersExceptions.length !== 0) && (
        <div>
          {exception.ersExceptions[0].ersExceptionName !== '' && (
            <div
              data-testid="grid-tables-data"
            >
              <CITableGrid
                headers={[
                  {
                    label: translate('list_exception.name'),
                    name: translate('list_exception.name'),
                    options: { breakPointWidth: 5 },
                    columnData: { field: 'name', render: renderExceptionName }
                  },
                  {
                    label: translate('list_exception.type'),
                    name: translate('list_exception.type'),
                    options: { breakPointWidth: 2 },
                    columnData: { field: translate('list_exception.type'), render: renderExceptionType, className: classes['exc-type'] }
                  },
                  {
                    label: translate('list_exception.last_modify'),
                    labelOptions: { helperText: `(${getLocaleDateTimeFormat()})`, helperTextClass: classes['last-modified'] },
                    name: lastModified,
                    sortOptions: {
                      callback: lastModifiedSortToggleHandler
                    },
                    options: { breakPointWidth: 2 },
                    columnData: { field: translate('list_exception.last_modify'), render: renderExceptionDate }
                  },
                  {
                    name: updatedBy,
                    label: translate('list_exception.modified_by'),
                    options: { breakPointWidth: 2 },
                    sortOptions: {
                      callback: updatedBySortToggleHandler
                    },
                    columnData: {
                      field: translate('list_exception.modified_by'), render: renderModifiedBy
                    }
                  },
                  {
                    label: '',
                    name: 'action',
                    options: { breakPointWidth: 1 },
                    columnData: { field: 'action', render: (row) => renderExceptionIconButtons({ row, viewBtnClickHandler, editBtnClickHandler, permission: permissionObject.permission }), className: classes['button-alignment'] }
                  },
                ]}
                footer={footerObj}
                sorted={{ sortedName: queryParams.sort ?? sortedName, sortedOrder: queryParams.type ?? sortedOrder }}
                tableData={exception.ersExceptions}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExceptionTable;
